import { useEffect, useState } from 'react';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import { useTenantContext } from '../components/App/AppTenant';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import EqxButton from '../shared/components/EqxButton/EqxButton';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxIcon from '../shared/components/EqxIcon';
import Tile, { EqxTileBlank } from '../shared/components/EqxTile/EqxTile';
import TileGroup from '../shared/components/EqxTile/EqxTileGroup';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';
import { formatTime, formatTimeDifferenceHours } from '../shared/utils/formatDate';

// Inner component that handles the tile rendering
const TileContent = () => {
    const { user } = useMainLayoutContext();
    const { isOffline, isFileCached } = useOffline();
    const tenant = useTenantContext();
    const api = useApi();
    const { response: timelogStatus, loading: timelogLoading, reset: resetTimelog } = useApiEndpoint(api, api.endpoints.timelogStatus(), true);
    const [isAvailable, setIsAvailable] = useState({
        controlledDocuments: false,
        trainingRecords: false,
        continuumForms: false,
        absences: false,
        timelog: false,
        serviceOrders: false,
        payslips: false,
        expenses: false
    });

    useEffect(() => {
        if (isOffline) {
            setIsAvailable({
                controlledDocuments: isFileCached('/controlled-documents'),
                trainingRecords: isFileCached('/training-records'),
                continuumForms: isFileCached('/continuum-forms'),
                absences: isFileCached('/absences/holiday-entitlements') || isFileCached('/absences/unassigned'),
                timelog: isFileCached('/timelog/get-status'),
                serviceOrders: isFileCached('/service-orders/today') || isFileCached('/service-orders/future'),
                payslips: isFileCached('/payslips'),
                expenses: isFileCached('/additional-payments/pending') || isFileCached('/additional-payments/processed')
            });
        } else {
            // When online, all routes are available
            setIsAvailable({
                controlledDocuments: true,
                trainingRecords: true,
                continuumForms: true,
                absences: true,
                timelog: true,
                serviceOrders: true,
                payslips: true,
                expenses: true
            });
        }
    }, [isOffline]);

    const mainContext = useMainLayoutContext()
    const hasStaffID = mainContext.user?.staffID ? true : false
    const canAccessLMS = mainContext.permissions?.canAccessLMS
    const canClockInOut = mainContext.permissions?.canClockInOut

    // Poll server for latest timelog status every 60s
    useEffect(() => {
        const intervalId = setInterval(() => resetTimelog(true), 60000);
        return () => clearInterval(intervalId);
    }, [resetTimelog]);

    return (
        <>
            <h3 className="display-6 mb-4">Hello{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.</h3>
            <EqxCard isSection={true} collapsible={false}>
                <div className="d-flex">
                    <div className="text-md-end mt-2 mt-md-0 me-2">
                        <span
                            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${timelogLoading && !timelogStatus ? 'bg-secondary' : (timelogStatus?.clockedIn ? 'bg-success' : 'bg-danger')
                                }`}
                            style={{ width: '40px', height: '40px' }}
                        >
                            <EqxIcon className="text-white fs-5" fa="far fa-clock" />
                        </span>
                    </div>
                    <div className="flex-grow-1 ps-2">
                        <h6 className="mb-0">Clock In Summary</h6>
                        <div className="text-muted">
                            {timelogLoading && !timelogStatus ? (
                                'Checking...'
                            ) : (
                                timelogStatus?.clockedIn ? (
                                    <>
                                        You clocked in at <strong>{formatTime(timelogStatus.timelog?.start)}</strong> and have worked for <strong>{formatTimeDifferenceHours(new Date(), timelogStatus.timelog?.start, undefined, true, false)}</strong>
                                    </>
                                ) : (
                                    'You are currently not clocked in'
                                )
                            )}
                        </div>
                    </div>
                    {canClockInOut && (
                        <div className="ps-2">
                            <EqxButton className="mt-1" variant="primary" size="sm" href="/timelog">More</EqxButton>
                        </div>
                    )}
                </div>
            </EqxCard >
            <TileGroup size="xs">
                <Tile
                    className="col-6 col-lg-3"
                    link="/hr"
                    title="HR"
                    icon="tile-icon fas fa-users"
                />
                <Tile
                    className="col-6 col-lg-3"
                    link="/work"
                    title="Work"
                    icon="tile-icon fas fa-briefcase"
                />
                <Tile
                    className="col-6 col-lg-3"
                    link="/docs"
                    title="Docs"
                    icon="tile-icon fas fa-file-alt"
                />


                {tenant.isLMSEnabled && canAccessLMS ? (
                    <Tile
                        className="col-6 col-lg-3"
                        link="/lms"
                        title="Learning Management System"
                        icon="fas fa-graduation-cap"
                    />
                ) : (
                    <EqxTileBlank className="col-6 col-lg-3" />
                )}



            </TileGroup>
        </>
    );
};

// Outer component that handles the layout
interface PageIndexProps { }
const PageIndex = function (props: PageIndexProps) {
    return (
        <MainLayout pageTitle="Overview">
            <TileContent />
        </MainLayout>
    );
};

export { PageIndex as default };

